import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import AssistantPanel from '../workflow/assistant/AssistantPanel';
import { HELP_ASK_EVENT, peekPendingAsk } from '../../help/helpBus';
import IconWrapper from '../common/IconWrapper';
import { AI_DRAWER_LIMITS, setAiDrawer, useAiDrawer } from './aiDrawerState';

/**
 * AI 에이전트 창 — 디자이너 맨 오른쪽에서 부드럽게 펼쳐진다.
 *
 *   · 노드 속성을 가리지 않고 **옆으로 밀면서** 열린다(같은 가로줄의 형제라 캔버스·속성이 좁아진다)
 *   · 폭이 바뀌는 동안 안쪽은 고정 폭 — 글자가 접혔다 펴지며 출렁이지 않고, 창이 통째로 밀려 들어온다
 *   · 대화는 처음 열 때 마운트하고 닫아도 남겨 둔다(대화 상태 유지)
 *   · 도움말 「AI 에게 물어보기」가 오면 저절로 연다 (help/helpBus)
 */
const AiDrawer: React.FC = () => {
  const { t } = useTranslation();
  const { open, width } = useAiDrawer();
  const [mounted, setMounted] = useState(open);
  // 닫힘 애니메이션이 끝나면 안쪽을 숨긴다 — 닫힌 창 안으로 Tab 초점이 들어가지 않게
  const [hidden, setHidden] = useState(!open);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (open) { setMounted(true); setHidden(false); }
  }, [open]);

  useEffect(() => {
    if (peekPendingAsk()) setAiDrawer({ open: true });
    const onAsk = () => setAiDrawer({ open: true });
    window.addEventListener(HELP_ASK_EVENT, onAsk);
    return () => window.removeEventListener(HELP_ASK_EVENT, onAsk);
  }, []);

  /** 왼쪽 가장자리를 끌어 폭 조절 — 끄는 동안은 애니메이션을 끈다(손을 바로 따라오게) */
  const widthRef = useRef(width);
  widthRef.current = width;
  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const from = widthRef.current;
    setDragging(true);
    const move = (ev: MouseEvent) => {
      const w = from + (startX - ev.clientX);
      setAiDrawer({ width: Math.max(AI_DRAWER_LIMITS.minWidth, Math.min(AI_DRAWER_LIMITS.maxWidth, w)) });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setDragging(false);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, []);

  return (
    <div
      className={classNames(
        'shrink-0 h-full overflow-hidden relative',
        !dragging && 'transition-[width] duration-300 ease-in-out',
      )}
      style={{ width: open ? width : 0 }}
      onTransitionEnd={(e) => { if (e.target === e.currentTarget && !open) setHidden(true); }}
      aria-hidden={!open}
    >
      <div
        className={classNames('absolute inset-y-0 left-0 flex border-l border-gray-200 bg-white', hidden && 'invisible')}
        style={{ width }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          title={t('layout:ai.resize')}
          onMouseDown={startDrag}
          onDoubleClick={() => setAiDrawer({ width: AI_DRAWER_LIMITS.defaultWidth })}
          className="shrink-0 w-1.5 h-full cursor-col-resize bg-gray-200 hover:bg-blue-400 transition-colors"
        />
        <div className="flex-1 min-w-0 flex flex-col min-h-0" data-point="panel:ai">
          <div className="shrink-0 flex items-center justify-between h-[37px] px-3 border-b border-gray-200 bg-gray-100">
            <span className="text-sm font-medium text-gray-700">{t('layout:ai.title')}</span>
            <button
              type="button"
              className="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-200"
              title={t('layout:ai.close')}
              onClick={() => setAiDrawer({ open: false })}
            >
              <IconWrapper icon={FiX} size={16} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {mounted && <AssistantPanel />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDrawer;
